import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button as MuiButton,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Cloud as CloudIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import UnifiedSamplingDialog from './UnifiedSamplingDialog';
import { useToast } from '../../context/ToastContext';

interface KeywordsPanelProps {
  clientId?: string;
  workflowId?: string;
  keywords: string[];
  onKeywordsChange: (keywords: string[]) => void;
  showSnowflakeSampling?: boolean;
}

const KeywordsPanel: React.FC<KeywordsPanelProps> = ({
  clientId,
  workflowId,
  keywords,
  onKeywordsChange,
  showSnowflakeSampling = true,
}) => {
  const { showToast } = useToast();
  const keywordInputRef = useRef<HTMLInputElement>(null);
  const [showSamplingDialog, setShowSamplingDialog] = useState(false);
  const [showAddKeywordsDialog, setShowAddKeywordsDialog] = useState(false);

  // Handler for Snowflake sampled keywords
  const handleSnowflakeKeywordsLoaded = (sampledKeywords: string[]) => {
    const validKeywords = sampledKeywords.filter(keyword => 
      keyword !== null && 
      keyword !== undefined && 
      typeof keyword === 'string' && 
      keyword.trim().length > 0
    );
    
    // Deduplicate keywords (case-sensitive)
    const uniqueKeywords = Array.from(new Set(validKeywords));
    
    if (uniqueKeywords.length < validKeywords.length) {
      const duplicateCount = validKeywords.length - uniqueKeywords.length;
      showToast(`Removed ${duplicateCount} duplicate keyword${duplicateCount > 1 ? 's' : ''} from sampling`, 'info');
    }
    
    onKeywordsChange(uniqueKeywords);
    setShowSamplingDialog(false);
  };

  const handleAddKeywords = () => {
    const inputValue = keywordInputRef.current?.value || '';
    const rawKeywords = inputValue
      .split('\n')
      .map(k => k.trim())
      .filter(k => k);  // Remove empty strings
    
    // Deduplicate within the new keywords
    const uniqueNewKeywords = Array.from(new Set(rawKeywords));
    
    // Filter out keywords that already exist in the list
    const keywordsToAdd = uniqueNewKeywords.filter(k => !keywords.includes(k));
    const alreadyExistCount = uniqueNewKeywords.length - keywordsToAdd.length;
    
    // Show notification about duplicates
    const duplicatesInInput = rawKeywords.length - uniqueNewKeywords.length;
    if (duplicatesInInput > 0) {
      showToast(`Removed ${duplicatesInInput} duplicate keyword${duplicatesInInput > 1 ? 's' : ''} from input`, 'info');
    }
    if (alreadyExistCount > 0) {
      showToast(`Skipped ${alreadyExistCount} keyword${alreadyExistCount > 1 ? 's' : ''} already in list`, 'info');
    }
    
    if (keywordsToAdd.length > 0) {
      onKeywordsChange([...keywords, ...keywordsToAdd]);
      showToast(`Added ${keywordsToAdd.length} keyword${keywordsToAdd.length > 1 ? 's' : ''}`, 'success');
      if (keywordInputRef.current) {
        keywordInputRef.current.value = '';
      }
      setShowAddKeywordsDialog(false);
    } else if (rawKeywords.length > 0) {
      showToast('No new keywords to add', 'warning');
    }
  };

  const handleCancelAddKeywords = () => {
    if (keywordInputRef.current) {
      keywordInputRef.current.value = '';
    }
    setShowAddKeywordsDialog(false);
  };

  const handleRemoveKeyword = (index: number) => {
    onKeywordsChange(keywords.filter((_, i) => i !== index));
  };

  const handleClearAllKeywords = () => {
    onKeywordsChange([]);
  };

  return (
    <>
      <Card sx={{ height: '100%', maxHeight: 500, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Keywords Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
            <Typography variant="h6">
              Keywords * ({keywords.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {showSnowflakeSampling && clientId && workflowId && (
                <MuiButton
                  variant="outlined"
                  size="small"
                  startIcon={<CloudIcon />}
                  onClick={() => setShowSamplingDialog(true)}
                >
                  Configure Unified Sampling
                </MuiButton>
              )}
              <MuiButton
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowAddKeywordsDialog(true)}
              >
                Add Keywords
              </MuiButton>
              {keywords.length > 0 && (
                <MuiButton
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={handleClearAllKeywords}
                  sx={{
                    borderColor: 'error.main',
                    color: 'error.main',
                    '&:hover': {
                      borderColor: 'error.dark',
                      backgroundColor: 'error.light',
                      color: 'error.dark',
                    },
                  }}
                >
                  Clear All
                </MuiButton>
              )}
            </Box>
          </Box>

          {/* Selected Keywords List */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Paper variant="outlined" sx={{ p: 1, flex: 1, overflow: 'auto', minHeight: 200 }}>
              <List dense>
                {keywords.map((keyword, index) => (
                  <ListItem 
                    key={index}
                    sx={{
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText primary={keyword} />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveKeyword(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        </CardContent>
      </Card>

      {/* Unified Snowflake Sampling Dialog */}
      {showSnowflakeSampling && clientId && workflowId && (
        <UnifiedSamplingDialog
          open={showSamplingDialog}
          onClose={() => setShowSamplingDialog(false)}
          clientId={clientId}
          workflowId={workflowId}
          onKeywordsLoaded={handleSnowflakeKeywordsLoaded}
        />
      )}

      {/* Add Keywords Dialog */}
      <Dialog 
        open={showAddKeywordsDialog} 
        onClose={handleCancelAddKeywords}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Keywords</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={10}
            fullWidth
            placeholder="Enter keywords (one per line)"
            inputRef={keywordInputRef}
            defaultValue=""
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleCancelAddKeywords}>
            Cancel
          </MuiButton>
          <MuiButton 
            onClick={handleAddKeywords} 
            variant="contained"
          >
            Add
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KeywordsPanel;

